import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  UploadCloud, Loader2, CheckCircle, XCircle, FileArchive,
  Eye, Clock, Library, X, Sparkles, Package, Timer, Download,
} from 'lucide-react';
import { scormService, ScormPackageListItem } from '@/services/scorm.service';
import { ADMIN_COURSE_EDIT, ADMIN_COURSE_CATALOG } from '@/constants/constant-routes';
import Pagination from '@/components/ui/Pagination';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { SCORM_IMPORT_PERMISSION, COURSE_CATALOG_PERMISSION } from '@/constants/constant-permissions';

const POLL_INTERVAL_MS = 5000;
const DEFAULT_PAGE_SIZE = 5;

// Absolute upload timestamp, e.g. "Jun 17, 2026, 4:28 PM".
function formatDateTime(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '';
  }
}

// How long the ingest took (createdAt → updatedAt), e.g. "1m 12s" / "45s" / "<1s".
function formatProcessingTime(start?: string, end?: string): string {
  if (!start || !end) return '';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return '<1s';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

// Stated on the dropzone as well as enforced, so the two cannot drift apart.
const MAX_SIZE_MB = 90;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

function StatusPill({ status }: { status: string }) {
  if (status === 'ready') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"><CheckCircle className="w-3.5 h-3.5" /> Ready</span>;
  if (status === 'failed') return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700"><XCircle className="w-3.5 h-3.5" /> Failed</span>;
  return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Processing</span>;
}

// The primary display title for a package: its materialized course title, else the filename.
function packageTitle(pkg: ScormPackageListItem): string {
  return pkg.courses?.[0]?.title || pkg.original_filename || 'SCORM package';
}

// Full-page loading skeleton mirroring the header / upload / side-list layout.
function ScormPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-brand-blue-600 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-white/20 animate-pulse" />
          <div className="space-y-2">
            <div className="h-6 w-56 rounded bg-white/20 animate-pulse" />
            <div className="h-4 w-80 rounded bg-white/10 animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-white/10 rounded-lg p-4 space-y-2">
              <div className="h-7 w-12 rounded bg-white/20 animate-pulse" />
              <div className="h-4 w-24 rounded bg-white/10 animate-pulse" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Upload */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="rounded-2xl border-2 border-dashed border-gray-200 p-12 flex flex-col items-center gap-4">
              <SkeletonLoader type="custom" className="h-16 w-16 rounded-2xl" />
              <SkeletonLoader type="title" className="w-56" />
              <SkeletonLoader type="text" className="w-72" />
            </div>
            <SkeletonLoader type="custom" className="h-12 w-full rounded-xl mt-5" />
          </div>
        </div>

        {/* Side list */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <SkeletonLoader type="text" className="h-5 w-40" />
              <SkeletonLoader type="text" className="h-4 w-12" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: DEFAULT_PAGE_SIZE }).map((_, i) => (
                <div key={i} className="rounded-xl border border-gray-200 p-3.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 flex-1">
                      <SkeletonLoader type="custom" className="h-8 w-8 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <SkeletonLoader type="text" className="h-4 w-3/4" />
                        <SkeletonLoader type="text" className="h-3 w-1/3" />
                      </div>
                    </div>
                    <SkeletonLoader type="custom" className="h-6 w-20 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ScormUploadPage() {
  // Page access: SCORM import read OR write.
  usePermissionRouteAccess([
    SCORM_IMPORT_PERMISSION.SCORM_IMPORT_READ,
    SCORM_IMPORT_PERMISSION.SCORM_IMPORT_WRITE,
  ]);
  // Upload/Import action requires SCORM write.
  const canUpload = useHasPermission([SCORM_IMPORT_PERMISSION.SCORM_IMPORT_WRITE]);
  // "View all courses" links require Course Catalog read OR write (cross-page).
  const canViewCatalog = useHasPermission([
    COURSE_CATALOG_PERMISSION.COURSE_CATALOG_READ,
    COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE,
  ]);
  // "View Course" (opens the course editor) requires Course Catalog write only.
  const canEditCourse = useHasPermission([COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE]);

  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [justUploadedId, setJustUploadedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { data: metrics, refetch: refetchMetrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['scorm-metrics'],
    queryFn: () => scormService.getMetrics(),
    refetchOnWindowFocus: false,
  });

  const { data: list, refetch: refetchList, isLoading: listLoading } = useQuery({
    queryKey: ['scorm-packages', page, pageSize],
    queryFn: () => scormService.listPackages(pageSize, (page - 1) * pageSize),
    refetchOnWindowFocus: false,
  });

  const packages = list?.packages || [];
  const totalCount = list?.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Only one package may be imported at a time: block while uploading or anything is processing.
  const hasProcessing = (metrics?.processing || 0) > 0;
  const busy = uploading || hasProcessing;
  // The dropzone is interactive only with write permission and when not busy.
  const uploadBlocked = busy || !canUpload;

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);

  // Poll metrics + the current page while anything is processing so cards transition live.
  useEffect(() => {
    if (hasProcessing && !pollRef.current) {
      pollRef.current = setInterval(() => { refetchMetrics(); refetchList(); }, POLL_INTERVAL_MS);
    } else if (!hasProcessing) {
      stopPolling();
    }
  }, [hasProcessing, refetchMetrics, refetchList]);

  // Clear any previously-selected file + the input on rejection, so a rejected
  // pick can't leave a stale file selected (and uploadable) behind the error.
  const rejectFile = (message: string) => {
    setError(message);
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const pickFile = (f: File | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) {
      rejectFile('Please select a .zip SCORM package.');
      return;
    }
    console.log(`[SCORM PickFile] Selected: ${f.name}, Size: ${f.size} bytes, Max Limit: ${MAX_SIZE_BYTES} bytes`);
    if (f.size > MAX_SIZE_BYTES) {
      rejectFile(`File size exceeds the ${MAX_SIZE_MB} MB limit. Please select a smaller SCORM package.`);
      return;
    }
    setError(null);
    setFile(f);
  };

  const onUpload = async () => {
    if (!file || busy || !canUpload) return;
    setUploading(true); setError(null); setUploadProgress(0);
    try {
      const res = await scormService.uploadPackage(file, undefined, (p) => setUploadProgress(p));
      setJustUploadedId(res.package_id);
      // Clear the dropzone — the package now appears in the side list as it processes.
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setPage(1);
      await Promise.all([refetchMetrics(), refetchList()]);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Full-page skeleton until both the metrics and the first page have loaded.
  if (metricsLoading || listLoading) return <ScormPageSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header with metric cards */}
      <div className="bg-brand-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <FileArchive className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Import SCORM Course</h1>
              <p className="text-brand-blue-100">Upload a SCORM 1.2 package — it becomes a draft course automatically.</p>
            </div>
          </div>
          {canViewCatalog ? (
            <Link
              to={ADMIN_COURSE_CATALOG}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
            >
              <Library className="w-5 h-5" />
              <span className="font-medium">View All Courses</span>
            </Link>
          ) : (
            <span
              className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg opacity-50 cursor-not-allowed"
              title="You do not have permission to view courses"
            >
              <Library className="w-5 h-5" />
              <span className="font-medium">View All Courses</span>
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{metrics?.total ?? 0}</div>
            <div className="text-sm text-brand-blue-200">Total Imports</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{metrics?.ready ?? 0}</div>
            <div className="text-sm text-brand-blue-200">Success</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="text-2xl font-bold">{metrics?.failed ?? 0}</div>
            <div className="text-sm text-brand-blue-200">Failed</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Upload */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div
              onDragOver={(e) => { e.preventDefault(); if (!uploadBlocked) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!uploadBlocked) pickFile(e.dataTransfer.files?.[0] || null); }}
              onClick={() => !uploadBlocked && inputRef.current?.click()}
              title={!canUpload ? 'You do not have permission to import SCORM packages' : undefined}
              className={`relative flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-12 transition-all
                ${dragOver ? 'border-brand-blue-500 bg-brand-blue-50 scale-[1.01]' : 'border-gray-300 hover:border-brand-blue-400 hover:bg-brand-blue-50/40'}
                ${uploadBlocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className={`p-4 rounded-2xl mb-4 transition-colors ${dragOver ? 'bg-brand-blue-200' : 'bg-brand-blue-100'}`}>
                <UploadCloud className="w-10 h-10 text-brand-blue-500" />
              </div>
              <p className="text-gray-800 font-semibold text-lg">
                {dragOver ? 'Drop to upload' : 'Drag & drop your .zip here'}
              </p>
              <p className="text-sm text-gray-400 mt-1">or <span className="text-brand-blue-600 font-medium">browse files</span> · SCORM 1.2 · .zip up to {MAX_SIZE_MB} MB</p>
              <input ref={inputRef} type="file" accept=".zip" className="hidden" onChange={(e) => pickFile(e.target.files?.[0] || null)} disabled={uploadBlocked} />
            </div>

            {/* One-at-a-time hint */}
            {hasProcessing && !uploading && (
              <div className="mt-4 flex items-center gap-2 text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> A package is still processing — please wait before importing another.
              </div>
            )}

            {/* Selected file chip */}
            {file && (
              <div className="mt-4 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-brand-blue-100 rounded-lg"><FileArchive className="w-5 h-5 text-brand-blue-500" /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-400">{formatBytes(file.size)}</p>
                  </div>
                </div>
                {!busy && (
                  <button onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ''; }} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            )}

            {error && <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2"><XCircle className="w-4 h-4" /> {error}</div>}

            <button
              onClick={onUpload}
              disabled={!file || busy || !canUpload}
              title={!canUpload ? 'You do not have permission to import SCORM packages' : undefined}
              className="mt-5 w-full bg-brand-blue-600 hover:bg-brand-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-sm"
            >
              {uploading ? <><Loader2 className="w-5 h-5 animate-spin" /> {uploadProgress < 100 ? 'Uploading…' : 'Processing…'}</> : <><Sparkles className="w-5 h-5" /> Import Course</>}
            </button>
          </div>
        </div>

        {/* Imported packages — side panel */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-500" /> Imported Packages
              </h2>
              {canViewCatalog ? (
                <Link to={ADMIN_COURSE_CATALOG} className="text-xs text-brand-blue-600 hover:text-brand-blue-800 font-medium">View all</Link>
              ) : (
                <span className="text-xs text-gray-400 cursor-not-allowed" title="You do not have permission to view courses">View all</span>
              )}
            </div>

            {packages.length === 0 ? (
              <div className="text-center py-10">
                <div className="p-3 bg-gray-50 rounded-full w-fit mx-auto mb-3"><FileArchive className="w-8 h-8 text-gray-300" /></div>
                <p className="text-sm text-gray-400">No imports yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {packages.map((pkg) => {
                  const isReady = pkg.status === 'ready';
                  const courseId = pkg.courses?.[0]?.id;
                  const canView = isReady && courseId;
                  return (
                    <div
                      key={pkg.package_id}
                      className={`rounded-xl border p-3.5 transition-all ${pkg.package_id === justUploadedId ? 'border-brand-blue-400 ring-2 ring-brand-blue-300' : 'border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="p-1.5 bg-brand-blue-50 rounded-lg flex-shrink-0"><FileArchive className="w-4 h-4 text-brand-blue-500" /></div>
                          <div className="min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight">{packageTitle(pkg)}</h3>
                            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3 flex-shrink-0" /> Uploaded {formatDateTime(pkg.createdAt)}</p>
                            {pkg.status !== 'processing' && pkg.updatedAt && (
                              <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><Timer className="w-3 h-3 flex-shrink-0" /> Processing time: {formatProcessingTime(pkg.createdAt, pkg.updatedAt)}</p>
                            )}
                          </div>
                        </div>
                        <StatusPill status={pkg.status} />
                      </div>

                      {pkg.status === 'failed' && pkg.error_message && (
                        <p className="mt-2.5 text-xs text-red-600 bg-red-50 rounded-lg px-2.5 py-2 line-clamp-2">{pkg.error_message}</p>
                      )}

                      {(canView || (isReady && pkg.original_zip_url)) && (
                        <div className="mt-3 flex gap-2">
                          {canView && (
                            <button
                              onClick={() => navigate(`${ADMIN_COURSE_EDIT}/${courseId}`)}
                              disabled={!canEditCourse}
                              title={!canEditCourse ? 'You do not have permission to open the course editor' : undefined}
                              className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-brand-blue-600 hover:bg-brand-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg py-2 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" /> View Course
                            </button>
                          )}
                          {/* Download enabled only after the package is fully processed (ready)
                              and only for users with SCORM import write permission. */}
                          {isReady && pkg.original_zip_url && (
                            canUpload ? (
                              <a
                                href={pkg.original_zip_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download the original SCORM .zip"
                                className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-brand-blue-700 border border-brand-blue-200 bg-brand-blue-50 hover:bg-brand-blue-100 rounded-lg py-2 transition-colors"
                              >
                                <Download className="w-3.5 h-3.5" /> Download .zip
                              </a>
                            ) : (
                              <span
                                title="You do not have permission to download SCORM packages"
                                className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium text-gray-400 border border-gray-200 bg-gray-50 rounded-lg py-2 cursor-not-allowed opacity-60"
                              >
                                <Download className="w-3.5 h-3.5" /> Download .zip
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {totalCount > 0 && (
                  <div className="-mx-6 -mb-6 mt-4 border-t border-gray-100">
                    <Pagination
                      currentPage={page}
                      totalPages={totalPages}
                      totalItems={totalCount}
                      itemsPerPage={pageSize}
                      onPageChange={setPage}
                      onItemsPerPageChange={(newSize) => {
                        setPageSize(newSize);
                        setPage(1);
                      }}
                      itemsPerPageOptions={[5, 10, 20, 50]}
                      compact
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
